import classNames from 'classnames';
import { isEmpty, omit, trim } from 'lodash';
import { InputHTMLAttributes, memo, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { MdClose } from 'react-icons/md';
import { QuestionProps } from '../../pa-prop-types';
import { QsOptions } from '../../patient-types';
import { propsToIgnore } from './qs-const';
import { useStaticLocales } from '../../../admin/hooks/locales/use-static-locale';
import { useSurveyState } from '../../hooks/use-survey-state';
import { useAutocomplete } from './hooks/useAutocomplete';

interface QsInputMultiTextProps extends InputHTMLAttributes<HTMLInputElement>, QuestionProps {
  isDisplayMode?: boolean;
  options?: QsOptions | null;
  onUpdate: (value?: string) => void;
  focusOnLoad?: boolean;
  restrictInput?: 'valid name' | null;
}

function QsInputMultiText(props: QsInputMultiTextProps) {
  const { 
    focusOnLoad, 
    isDisplayMode, 
    onUpdate, 
    className, 
    value, 
    options, 
    error, 
    restrictInput, 
    question 
  } = props;
  const { max, min } = options || {};
  const filteredProps = omit(props, [...propsToIgnore, 'restrictInput']);

  const { sltStr } = useStaticLocales();
  const inputRef = useRef<HTMLInputElement>(null);

  // surveyState 사용
  const [{ surveyState }, { setSurveyState }] = useSurveyState();

  // 질문 타입 체크 (medication 관련 질문인지 확인)
  const isMedicationQuestion = useMemo(() => {
    return question?.questionKey === 'q-dhx-description-athena' || 
           question?.questionKey === 'q-dhx-description' || 
           question?.questionKey?.includes('dhx');
  }, [question?.questionKey]);

  // 입력값 상태 관리
  const [inputValue, setInputValue] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  // medication 자동완성을 위한 간단한 로직
  const [hasProcessedMedication, setHasProcessedMedication] = useState(false);

  // 최신 값을 참조하기 위한 ref
  const inputValueRef = useRef(inputValue);
  const tagsRef = useRef(tags);

  // ref 업데이트
  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  useEffect(() => {
    tagsRef.current = tags;
  }, [tags]);

  // tags 상태 변경 추적
  useEffect(() => {
    console.log('Tags state changed:', tags);
  }, [tags]);

  // 안정적인 함수 참조를 위한 ref
  const setSurveyStateRef = useRef(setSurveyState);
  const onUpdateRef = useRef(onUpdate);
  
  // ref 업데이트
  useEffect(() => {
    console.log('Setting up onUpdateRef...');
    setSurveyStateRef.current = setSurveyState;
    onUpdateRef.current = (updateValue) => {
      console.log('onUpdateRef called with:', updateValue);
      console.log('updateValue type:', typeof updateValue);
      console.log('updateValue includes |:', updateValue && updateValue.includes('|'));
      
      // 자동완성된 값이면 직접 태그로 처리
      if (updateValue && updateValue.includes('|')) {
        const newTags = updateValue.split('|').filter(tag => trim(tag) !== '');
        console.log('Auto-filling tags directly:', newTags);
        setTags(newTags);
        setInputValue('');
        setHasProcessedMedication(true);
        console.log('Tags should be updated to:', newTags);
        // onUpdate는 나중에 호출해서 무한 루프 방지
        setTimeout(() => onUpdate(updateValue), 0);
      } else {
        console.log('Calling onUpdate directly for non-pipe value');
        onUpdate(updateValue);
      }
    };
  }, [onUpdate]); // tags 의존성 제거

  // useAutocomplete를 사용해서 medication 데이터만 가져오기 (autoFillValue는 호출하지 않음)
  useAutocomplete({
    isAllergyQuestion: false,
    isMedicationQuestion: isMedicationQuestion || false,
    isPharmacyQuestion: false,
    isPCPQuestion: false,
    isNestedAllergyQuestion: false,
    questionKey: question?.questionKey,
    surveyState,
    setSurveyStateRef,
    onUpdateRef,
  });

  // currentMedication 상태 업데이트 함수 (Past Medical History용)
  // medicationCurrent는 Regular Medication(q-medication-pta-describe)에서만 사용
  const updateCurrentMedicationState = useCallback((currentTags: string[]) => {
    const currentMedication = currentTags.join('|');

    console.log('currentMedication (Past Medical History):', currentMedication);

    setSurveyStateRef.current((prev: any) => ({
      ...prev,
      currentMedication, // Past Medical History는 currentMedication에만 설정
      // medicationCurrent는 설정하지 않음 - Regular Medication에서만 사용
    }));
  }, []);

  // value prop이 변경되면 tags와 inputValue 업데이트
  useEffect(() => {
    console.log('QsInputMultiText value changed:', value);
    if (value !== undefined) {
      const valueStr = String(value || '');
      console.log('Processing valueStr:', valueStr);
      
      // 현재 태그와 비교해서 다를 때만 업데이트
      const currentTagsStr = tags.join('|');
      if (valueStr !== currentTagsStr) {
        if (valueStr.includes('|')) {
          const newTags = valueStr.split('|').filter(tag => trim(tag) !== '');
          console.log('Setting tags from value prop:', newTags);
          setTags(newTags);
          setInputValue('');
        } else if (valueStr === '') {
          // 빈 값일 때만 태그 초기화
          console.log('Clearing tags for empty value');
          setTags([]);
          setInputValue('');
        } else {
          console.log('Setting inputValue from value prop:', valueStr);
          setTags([]);
          setInputValue(valueStr);
        }
      }
    }
  }, [value]); // tags 의존성 완전히 제거

  // surveyState.currentMedication이 변경될 때 태그 추가 (Past Medical History용)
  // medicationCurrent는 Regular Medication(q-medication-pta-describe)에서만 사용
  useEffect(() => {
    if (
      isMedicationQuestion && 
      surveyState?.currentMedication && 
      surveyState.currentMedication !== '' &&
      !hasProcessedMedication
    ) {
      console.log('Processing currentMedication (Past Medical History):', surveyState.currentMedication);
      
      // currentMedication을 태그로 변환
      const medicationTags = surveyState.currentMedication
        .split('|')
        .filter(tag => trim(tag) !== '');
      
      if (medicationTags.length > 0) {
        console.log('Setting medication tags from currentMedication:', medicationTags);
        setTags(medicationTags);
        setInputValue('');
        setHasProcessedMedication(true);
        
        // medication 자동완성 완료 후 onUpdate 호출 (setTimeout으로 지연)
        const combinedValue = medicationTags.join('|');
        setTimeout(() => {
          onUpdate(combinedValue);
        }, 0);
      }
    }
  }, [surveyState?.currentMedication, isMedicationQuestion, hasProcessedMedication, onUpdate]);


  // 포커스 온 로드
  useEffect(() => {
    if (focusOnLoad && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focusOnLoad]);

  // 태그 추가 함수
  const addTag = useCallback((tagValue: string) => {
    const trimmedValue = trim(tagValue);
    if (trimmedValue && !tags.includes(trimmedValue)) {
      const newTags = [...tags, trimmedValue];
      setTags(newTags);
      setInputValue('');
      
      // 부모 컴포넌트에 업데이트된 값 전달 (setTimeout으로 지연)
      const combinedValue = newTags.join('|');
      setTimeout(() => {
        onUpdate(combinedValue);
      }, 0);

      // surveyState에 currentMedication 업데이트
      if (isMedicationQuestion) {
        updateCurrentMedicationState(newTags);
        console.log('newTags', newTags);
      }
    }
  }, [tags, onUpdate, isMedicationQuestion, updateCurrentMedicationState]);

  // 태그 제거 함수
  const removeTag = useCallback((indexToRemove: number) => {
    const newTags = tags.filter((_, index) => index !== indexToRemove);
    setTags(newTags);
    
    // 부모 컴포넌트에 업데이트된 값 전달 (setTimeout으로 지연)
    const combinedValue = newTags.length > 0 ? newTags.join('|') : '';
    setTimeout(() => {
      onUpdate(combinedValue);
    }, 0);

    // surveyState에 currentMedication 업데이트
    if (isMedicationQuestion) {
      updateCurrentMedicationState(newTags);
    }
  }, [tags, onUpdate, isMedicationQuestion, updateCurrentMedicationState]);

  // Enter 키 핸들러 (제거됨 - 이제 + 버튼만 사용)
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter 키로 추가하는 기능 제거
  }, []);

  // 입력값 변경 핸들러
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);


  // 유효성 검사
  let valid = true;
  let errorMessage = null;

  if (valid && min != null) {
    if (tags.length < Number(min)) {
      valid = false;
      errorMessage = sltStr({ key: 'qs-multi-min-error' }).replace('{{min}}', min.toString());
    }
  }
  if (valid && max != null) {
    if (tags.length > Number(max)) {
      valid = false;
      errorMessage = sltStr({ key: 'qs-multi-max-error' }).replace('{{max}}', max.toString());
    }
  }

  const hasValue = tags.length > 0 || !isEmpty(trim(inputValue));

  return (
    <div className="QsInputMultiText">
      {errorMessage && <div className='text-error opacity-70 p-1'>{errorMessage}</div>}
      
      <div className="relative">
        {/* 입력 필드와 ADD 버튼이 포함된 컨테이너 */}
        <div className="relative">
          <input
            ref={inputRef}
            id={question?.questionKey}
            data-question-key={question?.questionKey}
            className={classNames(
              'input input-bordered w-full rounded-xl pr-20',
              className,
              hasValue && !isDisplayMode && !valid && 'input-error',
              error && 'input-error'
            )}
            {...filteredProps}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={sltStr({ key: 'qs-multi-placeholder' })}
          />
          {/* ADD 버튼 */}
          <button
            type="button"
            onClick={() => {
              if (trim(inputValue)) {
                addTag(inputValue);
              }
            }}
            disabled={!trim(inputValue)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 btn btn-sm add-btn"
            style={{ 
              height: '32px', 
              minHeight: '32px',
              borderRadius: '8px',
              backgroundColor: trim(inputValue) ? '#3555FC' : '#E5E7EB',
              borderColor: trim(inputValue) ? '#3555FC' : '#E5E7EB',
              cursor: trim(inputValue) ? 'pointer' : 'not-allowed',
              paddingLeft: '12px',
              paddingRight: '12px'
            }}
          >
            <span style={{ 
              color: trim(inputValue) ? "white" : "#9CA3AF",
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {sltStr({ key: 'btn-add', upper: true })}
            </span>
          </button>
        </div>
        
        {/* 태그들 */}
        {tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {tags.map((tag, index) => (
              <div
                key={index}
                className="badge badge-lg gap-2 px-3 badge-outline"
                style={{ 
                  backgroundColor: '#fff', 
                  color: '#3555FC', 
                  borderColor: '#3555FC',
                  height: '40px',
                  minHeight: '40px'
                }}
              >
                <span>{tag}</span>
                {!isDisplayMode && (
                  <button
                    type="button"
                    onClick={() => removeTag(index)}
                    className="btn btn-ghost btn-xs btn-circle"
                  >
                    <MdClose size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(QsInputMultiText);
